import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, Archive, ArchiveRestore, Mail, MailOpen, Inbox, Tag as TagIcon, ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import { useMessageFilters } from '@/hooks/useMessageFilters';
import { logger } from '@/lib/logger';
import AdminSearch from '@/components/admin/AdminSearch';
import AdminTabs from '@/components/admin/AdminTabs';
import ConfirmDialog from '@/components/admin/ConfirmDialog';

const statusPillClass = (status, archived) => {
  if (archived) return 'bg-white/10 text-white/60 border-white/10';
  if (status === 'New') return 'bg-[#F2B705]/15 text-[#F2B705] border-[#F2B705]/30';
  return 'bg-white/10 text-white/80 border-white/10';
};

export default function AdminMessages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('Active'); // Active | Unread | Read | Archived | All
  const [tagFilter, setTagFilter] = useState('');
  const [q, setQ] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [tagsDraft, setTagsDraft] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/messages');
      setItems(data || []);
      window.dispatchEvent(new Event('pg-refresh-unread'));
    } catch (err) { logger.warn('Failed to load messages', err); toast.error('Failed to load messages'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const { allTags, filtered } = useMessageFilters(items, { statusFilter, tagFilter, q });

  const selected = filtered.find((m) => m.id === selectedId) || filtered[0] || null;

  useEffect(() => {
    setNotesDraft(selected?.notes || '');
    setTagsDraft((selected?.tags || []).join(', '));
  }, [selected?.id]);

  const patch = async (id, body, quiet = false) => {
    try {
      const { data } = await api.patch(`/admin/messages/${id}`, body);
      setItems((prev) => prev.map((m) => (m.id === data.id ? data : m)));
      window.dispatchEvent(new Event('pg-refresh-unread'));
      return data;
    } catch (err) {
      if (!quiet) toast.error(err?.response?.data?.detail || 'Failed to update');
    }
  };

  const markRead = () => selected && patch(selected.id, { status: 'Read' });
  const markUnread = () => selected && patch(selected.id, { status: 'New' });
  const toggleArchive = () => selected && patch(selected.id, { archived: !selected.archived });
  const saveNotes = async () => {
    if (!selected) return;
    await patch(selected.id, { notes: notesDraft });
    toast.success('Notes saved.');
  };
  const saveTags = async () => {
    if (!selected) return;
    const arr = tagsDraft.split(',').map((s) => s.trim()).filter(Boolean);
    try {
      const { data } = await api.put(`/admin/messages/${selected.id}/tags`, { tags: arr });
      setItems((prev) => prev.map((m) => (m.id === data.id ? data : m)));
      toast.success('Tags updated.');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to update tags'); }
  };
  const del = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/messages/${confirmDelete}`);
      setItems((prev) => prev.filter((m) => m.id !== confirmDelete));
      setSelectedId(null);
      setConfirmDelete(null);
      window.dispatchEvent(new Event('pg-refresh-unread'));
      toast.success('Deleted.');
    } catch { toast.error('Failed to delete'); }
  };

  // Auto mark-as-read when opening a New message
  useEffect(() => {
    if (selected && selected.status === 'New' && !selected.archived) {
      patch(selected.id, { status: 'Read' }, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const clickChip = (tag) => setTagFilter(tagFilter === tag ? '' : tag);

  return (
    <div>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight inline-flex items-center gap-2"><Inbox className="w-7 h-7 text-[var(--pg-admin-accent)]" /> Messages</h1>
          <p className="mt-1 text-sm text-white/60">Inquiries from your contact form. Read, tag, archive, or delete.</p>
        </div>
        <AdminTabs
          className="-mx-4 sm:mx-0"
          options={['Active', 'Unread', 'Read', 'Archived', 'All'].map((f) => ({
            id: f,
            label: f,
            testid: `admin-messages-filter-${f.toLowerCase()}`,
          }))}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </div>

      <div className="mt-4">
        <AdminSearch
          testid="admin-messages-search"
          value={q}
          onChange={setQ}
          placeholder="Search by name, email, content, or tag..."
        />
      </div>

      {allTags.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-white/50 uppercase tracking-widest">Tags:</span>
          {allTags.map((t) => (
            <button key={t} onClick={() => clickChip(t)} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-colors ${tagFilter === t ? 'bg-[#F2B705] text-[#141414] border-[#F2B705] font-semibold' : 'border-white/10 text-white/70 hover:bg-white/5'}`}>
              <TagIcon className="w-3 h-3" /> {t}
            </button>
          ))}
          {tagFilter && <button onClick={() => setTagFilter('')} className="text-xs text-white/50 hover:text-white underline">clear</button>}
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-12 gap-4">
        <div data-testid={ADMIN.messagesTable} className={`lg:col-span-5 rounded-2xl border border-white/10 bg-[#141414] overflow-hidden max-h-[72vh] flex flex-col ${selectedId ? 'hidden lg:flex' : 'flex'}`}>
          {loading ? <div className="p-6 text-sm text-white/60">Loading...</div> :
            filtered.length === 0 ? (
              <div className="p-10 text-center text-white/60">
                <Inbox className="w-8 h-8 mx-auto mb-3 text-white/30" />No messages match.
              </div>
            ) : (
              <ul className="overflow-y-auto divide-y divide-white/5">
                {filtered.map((m) => {
                  const active = selected?.id === m.id;
                  return (
                    <li key={m.id}>
                      <button data-testid={ADMIN.messageRow(m.id)} onClick={() => setSelectedId(m.id)} className={`w-full text-left px-4 py-3 hover:bg-white/[0.03] transition-colors ${active ? 'bg-white/[0.05]' : ''}`}>
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            {m.status === 'New' && !m.archived && <span aria-hidden className="w-2 h-2 rounded-full bg-[#F2B705] shrink-0" />}
                            <span className={`truncate ${m.status === 'New' && !m.archived ? 'font-semibold text-white' : 'text-white/85'}`}>{m.name}</span>
                          </div>
                          <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded-full ${statusPillClass(m.status, m.archived)}`}>
                            {m.archived ? 'Archived' : m.status === 'New' ? 'Unread' : 'Read'}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-white/50 truncate">{m.email}{m.interest ? ` · ${m.interest}` : ''}</div>
                        <div className="mt-1 text-sm text-white/70 line-clamp-2">{m.message}</div>
                        {(m.tags || []).length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {m.tags.slice(0, 4).map((t) => (
                              <span key={t} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#C9A227]/20 text-[#E7D08A] border border-[#C9A227]/30"><TagIcon className="w-2.5 h-2.5" />{t}</span>
                            ))}
                          </div>
                        )}
                        <div className="mt-1 text-[11px] text-white/40">{new Date(m.created_at).toLocaleString()}</div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
        </div>

        <div data-testid={ADMIN.messageDetail} className={`lg:col-span-7 rounded-2xl border border-white/10 bg-[#141414] min-h-[60vh] flex flex-col ${selectedId ? 'flex' : 'hidden lg:flex'}`}>
          {!selected ? (
            <div className="m-auto text-white/50 text-sm">Select a message to view details.</div>
          ) : (
            <>
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-white/5 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <button onClick={() => setSelectedId(null)} data-testid="admin-message-back" className="lg:hidden inline-flex items-center gap-1 text-xs text-white/60 hover:text-white mb-2"><ArrowLeft className="w-3.5 h-3.5" /> Back to list</button>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-[#F2B705] text-[#141414] font-display shrink-0">{selected.name.charAt(0)}</span>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{selected.name}</div>
                      <div className="text-xs text-white/50 truncate">{selected.email}{selected.phone ? ` · ${selected.phone}` : ''}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-[11px] text-white/40">Received {new Date(selected.created_at).toLocaleString()}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded-full shrink-0 ${statusPillClass(selected.status, selected.archived)}`}>
                  {selected.archived ? 'Archived' : selected.status === 'New' ? 'Unread' : 'Read'}
                </span>
              </div>
              <div className="px-6 py-4 space-y-4 flex-1 overflow-y-auto">
                {selected.interest && (
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-white/50">Service interest</div>
                    <div className="text-sm mt-0.5">{selected.interest}</div>
                  </div>
                )}
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-white/50">Message</div>
                  <p className="mt-1 text-sm text-white/85 whitespace-pre-wrap leading-relaxed">{selected.message}</p>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-white/50 flex items-center gap-2"><TagIcon className="w-3 h-3" /> Tags</div>
                  <div className="mt-1 flex gap-2">
                    <input data-testid={ADMIN.messageTagsInput} value={tagsDraft} onChange={(e) => setTagsDraft(e.target.value)} placeholder="e.g. hot lead, tournament, corporate" className="flex-1 rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#F2B705] focus:ring-2 focus:ring-[#F2B705]/30 outline-none transition-colors" />
                    <button data-testid={ADMIN.messageTagsSave} onClick={saveTags} className="text-xs px-3 py-2 rounded-lg border border-white/10 hover:bg-white/5">Save tags</button>
                  </div>
                  <p className="mt-1 text-[11px] text-white/40">Comma-separated. Reuse tags to build filters.</p>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-white/50">Internal notes</div>
                  <textarea rows={3} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} placeholder="Add private notes for follow-up..." className="mt-1 w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#F2B705] focus:ring-2 focus:ring-[#F2B705]/30 outline-none transition-colors resize-none" />
                  <div className="mt-2 flex justify-end">
                    <button onClick={saveNotes} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/5">Save notes</button>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-white/5 flex flex-wrap items-center gap-2">
                {selected.status === 'New' ? (
                  <button data-testid={ADMIN.messageMarkRead} onClick={markRead} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 hover:bg-white/5 text-sm"><MailOpen className="w-4 h-4" /> Mark Read</button>
                ) : (
                  <button data-testid={ADMIN.messageMarkUnread} onClick={markUnread} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 hover:bg-white/5 text-sm"><Mail className="w-4 h-4" /> Mark Unread</button>
                )}
                <button data-testid={ADMIN.messageArchive} onClick={toggleArchive} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 hover:bg-white/5 text-sm">
                  {selected.archived ? <><ArchiveRestore className="w-4 h-4" /> Unarchive</> : <><Archive className="w-4 h-4" /> Archive</>}
                </button>
                <div className="grow" />
                <button data-testid={ADMIN.messageDelete} onClick={() => setConfirmDelete(selected.id)} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300 text-sm">
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          open={!!confirmDelete}
          onOpenChange={(v) => !v && setConfirmDelete(null)}
          title="Delete this message?"
          description="This cannot be undone. Consider archiving the message instead if you want to keep the record."
          confirmText="Delete message"
          destructive
          onConfirm={del}
          confirmTestId="admin-message-delete-confirm"
        />
      )}
    </div>
  );
}
